import type { FormEvent } from 'react';
import { useState } from 'react';

import type { IconList } from '@snowit/icetypes';

import * as cache from '@snowit/cache';
import useForms from '@snowit/hooks/useForms';
import useAuth from '@snowit/system/useAuth';
import useLanguage from '@snowit/system/useLanguage';
import useStyle from '@snowit/system/useStyle';
import {
  AUTH_TENANT_STORAGE_KEY,
  DEFAULT_AUTH_TENANT,
  resolveAuthTenant,
} from '@snowit/system/useTenants/utils';
import { Banner, Button, Grid, Input, InputPassword } from '@snowit/ui';

import Logo from '../Logo';

import cx from 'classnames';
import s from './Login.module.scss';

type LoginForm = {
  email: string;
  password: string;
  authTenant: string;
};

const Login = () => {
  const { t, te } = useLanguage();
  const { signIn } = useAuth();
  const { logo } = useStyle();
  const [loading, setLoading] = useState(false);
  const [mainError, setMainError] = useState('');

  const form = useForms<LoginForm>(
    {
      email: '',
      password: '',
      authTenant: cache.getLocal<string>(AUTH_TENANT_STORAGE_KEY, '') ?? '',
    },
    {
      validators: {
        email: ({ email }) => (email ? undefined : 'lzy.error.required'),
        password: ({ password }) => (password ? undefined : 'lzy.error.required'),
        authTenant: ({ authTenant }) => {
          if (!authTenant?.trim()) return undefined;
          return resolveAuthTenant(authTenant) ? undefined : 'lzy.error.auth_tenant_unknown';
        },
      },
    },
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    try {
      event.preventDefault();
      form.setDirty(true);
      if (form.hasErrors) return;
      setLoading(true);
      cache.setLocal<string>(AUTH_TENANT_STORAGE_KEY, form.state.authTenant, { forever: true });
      const authTenant = resolveAuthTenant(form.state.authTenant) ?? DEFAULT_AUTH_TENANT;
      await signIn(form.state.email, form.state.password, authTenant);
    } catch (error: any) {
      setMainError(error.code);
    } finally {
      setLoading(false);
    }
  };

  const renderBannerError = () => {
    return (
      <Banner
        classNames={{ wrapper: s.bannerError }}
        theme={'error'}
        text={t(mainError)}
        size={'sm'}
      />
    );
  };

  const renderInputEmail = () => {
    return (
      <Input
        name={'email'}
        label={t('lzy.label.email')}
        value={form.state.email}
        onChange={({ value }) => form.fill({ email: value })}
        error={Boolean(form.errors.email)}
        errorMsg={te(form.errors.email)}
      />
    );
  };

  const renderInputPassword = () => {
    return (
      <InputPassword
        name={'password'}
        label={t('lzy.input.label.password')}
        value={form.state.password}
        onChange={({ value }) => form.fill({ password: value })}
        error={Boolean(form.errors.password)}
        errorMsg={te(form.errors.password)}
      />
    );
  };

  const renderAuthTenant = () => {
    return (
      <Input
        name='authTenant'
        label={t('lzy.label.tenant')}
        value={form.state.authTenant}
        onChange={({ value }) => form.fill({ authTenant: value })}
        error={Boolean(form.errors.authTenant)}
        errorMsg={te(form.errors.authTenant)}
      />
    );
  };

  const renderFooter = () => {
    return (
      <footer className={s.footerVertical}>
        <Button
          classNames={{ button: s.btnFull }}
          label={t('lzy.action.sign_in')}
          theme={'fill'}
          bgColor={'primary'}
          type={'submit'}
          loading={loading}
        />
      </footer>
    );
  };

  const cells = [
    {
      key: 'banner_error',
      columnStart: 1,
      columnSpan: 1,
      content: renderBannerError,
      hide: !mainError,
    },
    {
      key: 'email',
      columnStart: 1,
      columnSpan: 1,
      content: renderInputEmail,
    },
    {
      key: 'password',
      columnStart: 1,
      columnSpan: 1,
      content: renderInputPassword,
    },
    {
      key: 'auth_tenant',
      columnStart: 1,
      columnSpan: 1,
      content: renderAuthTenant,
    },
    {
      key: 'footer',
      columnStart: 1,
      columnSpan: 1,
      content: renderFooter,
    },
  ];

  return (
    <main className={s.main}>
      <form className={cx(s.form)} onSubmit={handleSubmit}>
        <Logo className={s.logo} name={logo as IconList} maxHeight={3} />
        <Grid columns={1} cells={cells} gap={1} />
      </form>
    </main>
  );
};

export default Login;
